// See https://github.com/i18next/i18next/issues/1035

import i18next, { type TFunction, use as i18NextUse } from "i18next";
import { initReactI18next, useTranslation } from "react-i18next";
import { formatNumber, FormatNumberFunction } from "./helper";
import { de } from "date-fns/locale";
import type { Locale } from "date-fns";

import { german } from "./locales/";

export type JobRadLocale = Locale & {
  currency: string;
  currencySymbol: string;
  thousandSeparator: string;
  decimalSeparator: string;
};

const languages = {
  de: {
    locale: {
      ...de,
      currency: "EUR",
      currencySymbol: "€",
      thousandSeparator: ".",
      decimalSeparator: ",",
    } satisfies JobRadLocale,
    dateFormat: "dd.MM.yyyy",
    translation: german,
  },
} as const;

export const useI18n = (): {
  locale: JobRadLocale;
  t: TFunction;
  formatNumber: FormatNumberFunction;
} => {
  const { i18n, t } = useTranslation();
  const { locale } = languages[i18n.language as keyof typeof languages];

  const formatNumberI18n: FormatNumberFunction = (numberInput, options) =>
    formatNumber(numberInput, {
      ...options,
      thousandSeparator: locale.thousandSeparator,
      decimalSeparator: locale.decimalSeparator,
    });

  return { locale, t, formatNumber: formatNumberI18n };
};

export const resources = {
  de: { translation: languages.de.translation },
} as const;

i18NextUse(initReactI18next).init({
  lng: "de",
  fallbackLng: "de",
  interpolation: {
    escapeValue: false,
  },
  react: { transKeepBasicHtmlNodesFor: ["sup", "span"] },
  resources,
});

/**
 * Do not use this inside components. Instead, use the `useI18n` hook.
 * Reason: `useI18n` is more type-safe because it internally uses react-i18next
 * instead of i18next.
 */
const i18n = i18next;

export { formatNumber, type FormatNumberFunction, i18n };
